import { createRouter, createWebHistory } from "vue-router";
import appState from "@/appState.js";

const routes = [
  {
    path: "/",
    redirect: () => {
      let path, query;
      if (localStorage.getItem("redirect_uri")) {
        // if the user is coming to the site with a url already in place (and has returned from the login page)
        [ path, query ] = localStorage.getItem("redirect_uri").split("?");
        localStorage.removeItem("redirect_uri");
        return query ? `${path}?${query}` : { path, query };
      }
      const activatedMenuItems = appState.menuItems.map(mI => mI.items).flat().filter(mI => mI.isActivated);
      if (appState.user.preferences.appHomepage) {
        // if the user has set a specific homepage via the app settings panel...
        // either go to the saved homepage, or if they no longer have access, go to first activated menu item
        const appHomepage = JSON.parse(appState.user.preferences.appHomepage);
        const appHomepageMenuItem = activatedMenuItems.find(aMI => aMI.label === appHomepage.label || aMI.to === appHomepage.to);
        [ path, query ] = [ 
          appHomepageMenuItem ? appHomepageMenuItem.to : activatedMenuItems[0].to,
          appHomepageMenuItem ? appHomepageMenuItem.query : activatedMenuItems[0].query
        ];
      } else {
        // if no url already in place and user does not have a homepage set via the app settings panel, go to first activated menu item
        [ path, query ] = [ activatedMenuItems[0].to, activatedMenuItems[0].query ];
      }
      return { path, query };
    }
  },
  {
    path: "/kpi/dashboard",
    component: () => import("@/views/kpi/KPIDashboard.vue"),
    meta: { 
      title: "KPI Dashboard",
      requiredRoles: [ "ROLE_KPI_DASHBOARD" ],
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/zendesk/ticket-search",
    component: () => import("@/views/zendesk/ZendeskTicketSearch.vue"),
    meta: { 
      title: "Zendesk Ticket Search", 
      requiredRoles: [ "ROLE_VIEW_ZENDESK" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/zendesk/ticket-detail/:id",
    component: () => import("@/views/zendesk/ZendeskTicketDetail.vue"),
    meta: { 
      title: "Zendesk Ticket Detail", 
      requiredRoles: [ "ROLE_VIEW_ZENDESK" ], 
      requiresAllOrgCodes: true 
    }
  },
  {
    path: "/inventory/dashboard",
    component: () => import("@/views/inventory/InventoryDashboard.vue"),
    meta: { 
      title: "Inventory Dashboard", 
      requiredRoles: [ "ROLE_INVENTORY" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/inventory/search",
    component: () => import("@/views/inventory/InventorySearch.vue"),
    meta: { 
      title: "Inventory Search", 
      requiredRoles: [ "ROLE_INVENTORY" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/inventory/detail-search",
    component: () => import("@/views/inventory/InventoryDetailSearch.vue"),
    meta: { 
      title: "Inventory Detail Search", 
      requiredRoles: [ "ROLE_INVENTORY" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/inventory/adjustment-search",
    component: () => import("@/views/inventory/InventoryAdjustmentSearch.vue"),
    meta: { 
      title: "Inventory Adjustment Search",
      requiredRoles: [ "ROLE_VIEW_INVENTORY_ADJUSTMENTS" ],
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/inventory/reconciliation",
    component: () => import("@/views/inventory/InventoryReconciliation.vue"),
    meta: { 
      title: "Inventory Reconciliation", 
      requiredRoles: [ "ROLE_INVENTORY" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/material/search",
    component: () => import("@/views/material/MaterialSearch.vue"),
    meta: { 
      title: "Material Search", 
      requiredRoles: [ "ROLE_INVENTORY" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/material/detail",
    component: () => import("@/views/material/MaterialDetail.vue"),
    meta: { 
      title: "Material Detail", 
      requiredRoles: [ "ROLE_INVENTORY" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/material/import-detail",
    component: () => import("@/views/material/MaterialImportDetail.vue"),
    meta: { 
      title: "Material Import Detail", 
      requiredRoles: [ "ROLE_APPROVE_MATERIALS", "ROLE_IMPORT_MATERIALS" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/material/review",
    component: () => import("@/views/material/MaterialReview.vue"),
    meta: { 
      title: "Material Review", 
      requiredRoles: [ "ROLE_APPROVE_MATERIALS", "ROLE_IMPORT_MATERIALS" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/order/dashboard",
    component: () => import("@/views/order/OrderDashboard.vue"),
    meta: { 
      title: "Order Dashboard", 
      requiredRoles: [ "ROLE_OPERATIONS_DASHBOARD" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/order/metrics",
    component: () => import("@/views/order/OrderMetrics.vue"),
    meta: { 
      title: "Order Metrics", 
      requiredRoles: [ "ROLE_ORDER_METRICS" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/order/search",
    component: () => import("@/views/order/OrderSearch.vue"),
    meta: { 
      title: "Order Search", 
      requiredRoles: [ "ROLE_ORDERS" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/order/line-search",
    component: () => import("@/views/order/OrderLineSearch.vue"),
    meta: { 
      title: "Order Line Search", 
      requiredRoles: [ "ROLE_ORDERS" ], 
      requiresAllOrgCodes: false
    }
  },
  {
    path: "/order/create-inbound",
    component: () => import( "@/views/order/CreateInboundOrder.vue"),
    meta: { 
      title: "Create Inbound Order", 
      requiredRoles: [ "ROLE_INBOUND_ORDER_CREATION" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/order/create-outbound",
    component: () => import( "@/views/order/CreateOutboundOrder.vue"),
    meta: { 
      title: "Create Outbound Order", 
      requiredRoles: [ "ROLE_OUTBOUND_ORDER_CREATION" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/order/detail",
    component: () => import("@/views/order/OrderDetail.vue"),
    meta: { 
      title: "Order Detail", 
      requiredRoles: [ "ROLE_ORDERS" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/operations/dashboard",
    component: () => import("@/views/operations/OperationsDashboard.vue"),
    meta: { 
      title: "Operations Dashboard", 
      requiredRoles: [ "ROLE_LOAD_VIEWER", "ROLE_LOAD_EXECUTOR", "ROLE_LOAD_PLANNER" ],
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/administration/organization",
    component: () => import("@/views/administration/Organization.vue"),
    meta: { 
      title: "Organization Administration", 
      requiredRoles: [ "ROLE_ORG_ADMIN" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/administration/project",
    component: () => import("@/views/administration/Project.vue"),
    meta: { 
      title: "Project Administration", 
      requiredRoles: [ "ROLE_PROJECT_ADMIN" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/administration/user",
    component: () => import("@/views/administration/User.vue"),
    meta: { 
      title: "User Administration", 
      requiredRoles: [ "ROLE_USER_ADMIN" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/administration/warehouse",
    component: () => import("@/views/administration/Warehouse.vue"),
    meta: { 
      title: "Warehouse Administration", 
      requiredRoles: [ "ROLE_WHSE_ADMIN" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/administration/warehouse-matrix",
    component: () => import("@/views/administration/WarehouseMatrix.vue"),
    meta: { 
      title: "Warehouse Matrix Administration", 
      requiredRoles: [ "ROLE_WHSE_ADMIN" ],
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/administration/template",
    component: () => import("@/views/administration/Template.vue"),
    meta: { 
      title: "Template Administration", 
      requiredRoles: [ "ROLE_TEMPLATE_ADMIN", "ROLE_TEMPLATE_METADATA" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/administration/scheduled-report",
    component: () => import("@/views/administration/ScheduledReport.vue"),
    meta: { 
      title: "Scheduled Report Administration", 
      requiredRoles: [ "ROLE_SCHEDULED_REPORTS_ADMIN" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/administration/order-configuration",
    component: () => import("@/views/administration/OrderConfiguration.vue"),
    meta: { 
      title: "Order Configuration Administration", 
      requiredRoles: [ "ROLE_AUTOMATION_ORDER_CONFIG" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/engineering/partner",
    component: () => import("@/views/engineering/Partner.vue"),
    meta: { 
      title: "Partner Administration", 
      requiredRoles: [ "ROLE_TP_ADMIN" ],
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/engineering/partner-transaction",
    component: () => import("@/views/engineering/PartnerTransaction.vue"),
    meta: { 
      title: "Partner Transaction Administration", 
      requiredRoles: [ "ROLE_TP_ADMIN" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/engineering/payload-tracker",
    component: () => import("@/views/engineering/PayloadTracker.vue"),
    meta: { 
      title: "Payload Tracker", 
      requiredRoles: [ "ROLE_PAYLOAD_TRACKER" ], 
      requiresAllOrgCodes: true
    }
  },
  {
    path: "/data-insights/quicksight/:id",
    component: () => import("@/views/dataInsights/QuickSight.vue"),
    meta: { 
      title: "Data Insights", 
      requiredRoles: [ "ROLE_QUICKSIGHT" ], 
      requiresAllOrgCodes: false 
    }
  },
  {
    path: "/session-expired",
    component: () => import("@/views/SignedOut.vue"),
    meta: { 
      title: "Session Expired"
    }
  },
  {
    path: "/signed-out",
    component: () => import("@/views/SignedOut.vue"),
    meta: { 
      title: "Signed Out"
    }
  },
  {
    path: "/error/forbidden",
    component: () => import("@/views/error/Forbidden403.vue"),
    meta: { 
      title: "403: Forbidden"
    }
  },
  {
    path: "/:catchAll(.*)",
    component: () => import("@/views/error/NotFound404.vue"),
    meta: { 
      title: "404: Not Found"
    }
  }
];

const router = createRouter({ 
  history: createWebHistory(import.meta.env.BASE_URL), 
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.path !== from.path) return { top: 0 };
  }
});

export default router;